import React, { useEffect } from 'react';
import {
  CircleDollarSign,
  Hash,
  Layers3,
  ReceiptText,
  ShieldCheck,
  UserRound,
  WalletCards,
  X,
} from 'lucide-react';

import type { PassbookItem } from '../types';

interface TransactionDetailsDrawerProps {
  transaction: PassbookItem | null;
  open: boolean;
  onClose: () => void;
}

const formatCurrency = (value: number | null | undefined) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value?: string | null) => {
  if (!value) return '—';

  return new Date(value).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusClasses = (status?: string) => {
  const normalized = String(status || '').toLowerCase();

  if (['won', 'completed', 'approved', 'success'].includes(normalized)) {
    return 'bg-emerald-50 text-emerald-700';
  }

  if (
    ['lost', 'lose', 'failed', 'rejected', 'cancelled'].includes(
      normalized,
    )
  ) {
    return 'bg-red-50 text-red-700';
  }

  if (['pending', 'initiated'].includes(normalized)) {
    return 'bg-amber-50 text-amber-700';
  }

  return 'bg-slate-100 text-slate-700';
};

const getSelectedNumber = (item: PassbookItem): string => {
  const placedBet = item.bet_details?.placed_bets?.[0];
  const originalBet =
    item.bet_details?.result_data?.original_payload?.bets?.[0];

  const candidates = [
    placedBet?.combination,
    placedBet?.pana,
    placedBet?.digit,
    placedBet?.jodi,
    placedBet?.open_pana,
    placedBet?.open_digit,
    placedBet?.close_pana,
    placedBet?.close_digit,
    originalBet?.combination,
    originalBet?.pana,
    originalBet?.digit,
    originalBet?.jodi,
    originalBet?.open_pana,
    originalBet?.open_digit,
    originalBet?.close_pana,
    originalBet?.close_digit,
    item.bet_details?.jodi,
    item.bet_details?.open_pana,
    item.bet_details?.open_digit,
    item.bet_details?.close_pana,
    item.bet_details?.close_digit,
  ];

  const selected = candidates.find(
    (value) => value !== undefined && value !== null && value !== '',
  );

  return selected !== undefined && selected !== null
    ? String(selected)
    : '—';
};

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-5 border-b border-slate-100 py-3 last:border-b-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="break-words text-right text-sm font-semibold text-slate-900">
      {value ?? '—'}
    </span>
  </div>
);

const TransactionDetailsDrawer: React.FC<
  TransactionDetailsDrawerProps
> = ({ transaction, open, onClose }) => {
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open || !transaction) {
    return null;
  }

  const amount = Number(transaction.amount || 0);
  const isCredit = amount > 0;
  const isDebit = amount < 0;

  const ledgerSummary = transaction.ledger_summary || null;
  const ledgerMetadata = transaction.ledger_metadata || null;
  const walletLegs = Array.isArray(ledgerMetadata?.wallet_legs)
    ? ledgerMetadata.wallet_legs
    : [];

  return (
    <div className="fixed inset-0 z-[1100]">
      <button
        type="button"
        aria-label="Close transaction details"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="transaction-drawer-title"
        className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">
              Transaction
            </p>

            <h2
              id="transaction-drawer-title"
              className="mt-1 text-xl font-semibold text-slate-900"
            >
              Transaction Details
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Complete ledger and betting information.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-6">
          <div className="rounded-2xl bg-slate-900 p-5 text-white">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-slate-300">
                  Transaction Amount
                </p>

                <p
                  className={`mt-2 text-3xl font-semibold ${
                    isCredit
                      ? 'text-emerald-300'
                      : isDebit
                        ? 'text-red-300'
                        : 'text-white'
                  }`}
                >
                  {isCredit ? '+' : ''}
                  {formatCurrency(amount)}
                </p>
              </div>

              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(
                  transaction.status,
                )}`}
              >
                {transaction.status_display ||
                  transaction.status ||
                  'Unknown'}
              </span>
            </div>

            <p className="mt-4 text-sm text-slate-300">
              {transaction.description ||
                transaction.type_display ||
                transaction.type ||
                'Transaction'}
            </p>
          </div>

          <section className="mt-6">
            <div className="mb-3 flex items-center gap-2">
              <ReceiptText className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
                Transaction Information
              </h3>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white px-4">
              <DetailRow
                label="Reference"
                value={transaction.transaction_id || transaction.id}
              />
              <DetailRow
                label="Type"
                value={
                  transaction.type_display ||
                  transaction.transaction_type ||
                  transaction.type
                }
              />
              <DetailRow
                label="Gateway"
                value={transaction.gateway || '—'}
              />
              <DetailRow
                label="Created At"
                value={formatDate(transaction.created_at)}
              />
              <DetailRow
                label="Updated At"
                value={formatDate(transaction.updated_at)}
              />
            </div>
          </section>

          {ledgerSummary && (
            <section className="mt-6">
              <div className="mb-3 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
                  Ledger Summary
                </h3>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white px-4">
                <DetailRow
                  label="Title"
                  value={ledgerSummary.title || '—'}
                />
                <DetailRow
                  label="Subtitle"
                  value={ledgerSummary.subtitle || '—'}
                />
                <DetailRow
                  label="Source"
                  value={ledgerSummary.source || '—'}
                />
                <DetailRow
                  label="Primary Wallet"
                  value={ledgerMetadata?.primary_wallet_type || '—'}
                />
              </div>
            </section>
          )}

          <section className="mt-6">
            <div className="mb-3 flex items-center gap-2">
              <CircleDollarSign className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
                Balance Movement
              </h3>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Previous
                </p>
                <p className="mt-2 text-base font-semibold text-slate-900">
                  {formatCurrency(transaction.before_amount)}
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Amount
                </p>
                <p
                  className={`mt-2 text-base font-semibold ${
                    isCredit
                      ? 'text-emerald-600'
                      : isDebit
                        ? 'text-red-600'
                        : 'text-slate-900'
                  }`}
                >
                  {isCredit ? '+' : ''}
                  {formatCurrency(amount)}
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Current
                </p>
                <p className="mt-2 text-base font-semibold text-slate-900">
                  {formatCurrency(transaction.after_amount)}
                </p>
              </div>
            </div>
          </section>

          <section className="mt-6">
            <div className="mb-3 flex items-center gap-2">
              <UserRound className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
                Player
              </h3>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white px-4">
              <DetailRow
                label="Player ID"
                value={transaction.user?.id ?? '—'}
              />
              <DetailRow
                label="Name"
                value={transaction.user?.first_name || '—'}
              />
              <DetailRow
                label="Mobile"
                value={transaction.user?.mobile_number || '—'}
              />
            </div>
          </section>

          {walletLegs.length > 0 && (
            <section className="mt-6">
              <div className="mb-3 flex items-center gap-2">
                <WalletCards className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
                  Wallet Audit Trail
                </h3>
              </div>

              <div className="space-y-3">
                {walletLegs.map((leg: any) => {
                  const legAmount = Number(leg.amount_change || 0);
                  const legCredit = legAmount > 0;
                  const legDebit = legAmount < 0;

                  return (
                    <div
                      key={leg.wallet_transaction_id}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold capitalize text-slate-900">
                            {leg.wallet_type || 'Wallet'}
                          </p>
                          <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                            {leg.direction || 'movement'}
                          </p>
                        </div>

                        <p
                          className={`text-sm font-semibold ${
                            legCredit
                              ? 'text-emerald-600'
                              : legDebit
                                ? 'text-red-600'
                                : 'text-slate-700'
                          }`}
                        >
                          {legCredit ? '+' : ''}
                          {formatCurrency(legAmount)}
                        </p>
                      </div>

                      <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                        <span>
                          Before: {formatCurrency(Number(leg.balance_before))}
                        </span>
                        <span className="sm:text-right">
                          After: {formatCurrency(Number(leg.balance_after))}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {ledgerMetadata && (
            <section className="mt-6">
              <div className="mb-3 flex items-center gap-2">
                <Layers3 className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
                  Journal Audit
                </h3>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white px-4">
                <DetailRow
                  label="Journal ID"
                  value={ledgerMetadata.journal_id ?? '—'}
                />
                <DetailRow
                  label="Payment ID"
                  value={ledgerMetadata.payment_id ?? '—'}
                />
                <DetailRow
                  label="Bet Input ID"
                  value={ledgerMetadata.bet_input_id ?? '—'}
                />
                <DetailRow
                  label="Starline Bet ID"
                  value={ledgerMetadata.starline_bet_id ?? '—'}
                />
                <DetailRow
                  label="Idempotency Key"
                  value={ledgerMetadata.idempotency_key || '—'}
                />
              </div>
            </section>
          )}

          {transaction.type === 'bet' && (
            <section className="mt-6">
              <div className="mb-3 flex items-center gap-2">
                <Hash className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
                  Betting Details
                </h3>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white px-4">
                <DetailRow
                  label="Market"
                  value={transaction.market?.name || '—'}
                />
                <DetailRow
                  label="Bet Type"
                  value={transaction.bet_type?.name || '—'}
                />
                <DetailRow
                  label="Session"
                  value={
                    transaction.bet_details?.session ||
                    transaction.bet_details?.bet_type ||
                    '—'
                  }
                />
                <DetailRow
                  label="Selected Number"
                  value={getSelectedNumber(transaction)}
                />
                <DetailRow
                  label="Bet Amount"
                  value={formatCurrency(
                    transaction.bet_amount ??
                      Math.abs(Number(transaction.amount || 0)),
                  )}
                />
                <DetailRow
                  label="Win Amount"
                  value={formatCurrency(transaction.win_amount)}
                />
                <DetailRow
                  label="Payout Multiplier"
                  value={
                    transaction.app_bet_type?.payout_multiplier
                      ? `${transaction.app_bet_type.payout_multiplier}x`
                      : '—'
                  }
                />
              </div>
            </section>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </aside>
    </div>
  );
};

export default TransactionDetailsDrawer;
